import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import Tasks from '@/pages/Tasks';
import Inbox from '@/pages/Inbox';
import TaskBoardPage from '@/pages/TaskBoardPage';
import Brand from '@/pages/Brand';
import Files from '@/pages/Files';
import SitemapEditor from '@/pages/SitemapEditor';
import { DesignStudioEditor } from '@/pages/DesignStudioEditor';
import AccountSettings from '@/pages/AccountSettings';
import { OnboardingFlow, BrandPreview } from './components/brand';
import { Home } from './components/Home';
import { CrmHome } from './components/CrmHome';

import { safeLocalStorage } from '@/lib/storage-utils';

import { BrandProvider } from '@/context/BrandContext';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { WorkspaceProvider, useWorkspace } from '@/context/WorkspaceContext';
import { TeamChatProvider, useTeamChat } from '@/context/TeamChatContext';
import { DashboardProvider } from '@/context/DashboardContext';
import { Sidebar } from './components/Sidebar';
import { AppHeader, MobileTopBar, MobileBottomNav, MobileSubMenu } from './components/layout';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { Login } from './pages/Login';
import { Signup } from './pages/Signup';
import { ResetPassword } from './pages/ResetPassword';
import { AcceptInvitation } from './pages/AcceptInvitation';
import { PublicRoute } from './components/auth/PublicRoute';
import Calendar from './pages/Calendar';
import Timesheet from './pages/Timesheet';
import SupportTickets from './pages/SupportTickets';
import KnowledgeBase from './pages/KnowledgeBase';
import Contacts from './pages/Contacts';
import Leads from './pages/Leads';
import Pipelines from './pages/Pipelines';
import Proposals from './pages/Proposals';
import ProposalEditor from './pages/ProposalEditor';
import ProposalPreview from './pages/ProposalPreview';
import Hosting from './pages/Hosting';
import Sites from './pages/Sites';
import SiteDetails from './pages/SiteDetails';
import Projects from './pages/Projects';
import Forms from './pages/Forms';
import Services from './pages/Services';
import Invoices from './pages/Invoices';
import ProjectDetail from './pages/ProjectDetail';
import { PortalLayout } from './components/portal/PortalLayout';
import { PortalDashboard } from './pages/portal/PortalDashboard';
import { PortalProjects } from './pages/portal/PortalProjects';
import { PortalInbox } from './pages/portal/PortalInbox';
import PortalProposals from './pages/portal/PortalProposals';
import PortalProposalView from './pages/portal/PortalProposalView';
import { PortalAccountSettings } from './pages/portal/PortalAccountSettings';
import { PortalSupport } from './pages/portal/PortalSupport';
import { ErrorBoundary } from './components/ui/ErrorBoundary';
import { TopProgressBar } from '@/components/ui/TopProgressBar';

// Create the QueryClient outside of the App component to ensure caching works correctly
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000,    // 30 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function RootRedirect() {
  const { user, loading } = useAuth();
  const { isClient, loading: workspaceLoading } = useWorkspace();

  if (loading || (user && workspaceLoading)) {
    return null;
  }

  if (!user) return <Navigate to="/login" replace />;
  if (isClient) return <Navigate to="/dashboard" replace />;
  return <Navigate to="/home" replace />;
}

function CompanyRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/crm/clients?tab=companies&companyId=${id}`} replace />;
}

function BlueprintRedirect() {
  const location = useLocation();
  const target = location.pathname.replace(/^\/brand\/design-studio/, '/brand/blueprints');
  return <Navigate to={`${target}${location.search}`} replace />;
}

function ProposalLegacyEditRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/proposals/${id}/edit`} replace />;
}

function ProposalLegacyPreviewRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/proposals/${id}/preview`} replace />;
}

function AppContent() {
  const [isGlobalDarkMode, setIsGlobalDarkMode] = useState(() => {
    const savedTheme = safeLocalStorage.getItem('themePreference');
    if (savedTheme === 'light') return false;
    if (savedTheme === 'dark') return true;
    if (savedTheme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return true;
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileSubMenu, setShowMobileSubMenu] = useState(false);
  const [mobileSubMenuItems, setMobileSubMenuItems] = useState<any[]>([]);
  const [activeParentMenuId, setActiveParentMenuId] = useState<string | null>(null);
  const [activeParentMenuLabel, setActiveParentMenuLabel] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Apply dark class to document element when theme changes
  useEffect(() => {
    if (isGlobalDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isGlobalDarkMode]);

  // Sync sidebar collapsed state to body for nested components to read initial state
  useEffect(() => {
    document.body.setAttribute('data-sidebar-collapsed', String(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  const { isClient } = useWorkspace();
  const isAuthPage = ['/login', '/signup', '/reset-password'].some(path => location.pathname === path);
  const isSitemapEditor = location.pathname.startsWith('/brand/sitemaps/editor');
  const isDesignSystemEditor = location.pathname.startsWith('/brand/design-system/') || location.pathname.startsWith('/brand/design-studio/') || location.pathname.startsWith('/brand/blueprints/');
  const isProposalEditor = /^\/(?:boards\/)?proposals\/[^/]+\/edit$/.test(location.pathname);
  const isPortal = isClient;
  const shouldShowSidebar = !isPortal && !isAuthPage && !isSitemapEditor && !isDesignSystemEditor && !isProposalEditor && ['/home', '/brand', '/files', '/calendar', '/task-board', '/inbox', '/boards', '/crm', '/services', '/account-settings', '/sites', '/tickets', '/ticket', '/knowledge-base', '/forms', '/deals', '/proposals', '/invoices'].some(path => location.pathname.startsWith(path));

  // Get team chat context to check if a chat is open
  const { activeChannelId, activeDmId } = useTeamChat();

  // Hide navigation bars when viewing a chat on mobile
  const isInboxPage = location.pathname.startsWith('/inbox');
  const hasChatOpen = activeChannelId || activeDmId;
  const isInChatView = isMobile && isInboxPage && hasChatOpen;
  const isInboxListPage = isMobile && isInboxPage && !hasChatOpen;
  const isFileManagerPage = location.pathname.startsWith('/files');
  const isHomePage = location.pathname === '/home';
  const shouldShowMobileTopBar = shouldShowSidebar && isMobile && !isInChatView && !isFileManagerPage && !isInboxListPage && isHomePage;
  const shouldShowMobileBottomNav = shouldShowSidebar && isMobile && !isInChatView;

  const handleMobileSubMenuOpen = (menuId: string | null, subItems?: any[], menuLabel?: string) => {
    if (menuId && subItems && subItems.length > 0) {
      setMobileSubMenuItems(subItems);
      setShowMobileSubMenu(true);
      setActiveParentMenuId(menuId);
      setActiveParentMenuLabel(menuLabel || null);
    } else {
      setShowMobileSubMenu(false);
      setMobileSubMenuItems([]);
      setActiveParentMenuId(null);
      setActiveParentMenuLabel(null);
    }
  };

  // Close mobile sub menu when navigating to file manager
  useEffect(() => {
    if (isFileManagerPage) {
      setShowMobileSubMenu(false);
      setMobileSubMenuItems([]);
      setActiveParentMenuId(null);
      setActiveParentMenuLabel(null);
    }
  }, [isFileManagerPage]);

  return (
    <div className={`${isMobile ? 'h-screen' : 'min-h-screen'} bg-background`}>
      <TopProgressBar />
      {/* Desktop Sidebar */}
      {shouldShowSidebar && !isMobile && (
        <Sidebar
          isGlobalDarkMode={isGlobalDarkMode}
          setIsGlobalDarkMode={setIsGlobalDarkMode}
          isCollapsed={isSidebarCollapsed}
          setIsCollapsed={setIsSidebarCollapsed}
          onToggleSidebar={() => {
            setIsSidebarCollapsed(prev => {
              const next = !prev;
              window.dispatchEvent(new CustomEvent('toggle-secondary-sidebar', { detail: { collapsed: next } }));
              return next;
            });
          }}
          activeSubmenu={activeSubmenu}
          setActiveSubmenu={setActiveSubmenu}
        />
      )}

      {/* Mobile Top Bar */}
      {shouldShowMobileTopBar && (
        <MobileTopBar
          isGlobalDarkMode={isGlobalDarkMode}
          setIsGlobalDarkMode={setIsGlobalDarkMode}
        />
      )}

      {/* Main Content Area (header + routes) */}
      <div
        className={`flex-1 flex flex-col transition-all duration-300 ${isGlobalDarkMode ? 'bg-background' : 'bg-white'} ${(shouldShowMobileTopBar || shouldShowMobileBottomNav) && !isInChatView ? 'fixed left-0 right-0 overflow-y-auto mobile-hide-scrollbar' : ''}`}
        style={{
          marginLeft: shouldShowSidebar && !isMobile ? ((activeSubmenu || isFileManagerPage || isInboxPage) && !isSidebarCollapsed ? '18rem' : '4rem') : '0',
          top: isInChatView ? 0 : shouldShowMobileTopBar ? '3.5rem' : shouldShowMobileBottomNav ? '0' : undefined,
          bottom: isInChatView ? 0 : shouldShowMobileBottomNav ? '4rem' : undefined,
          height: isInChatView ? '100vh' : (shouldShowMobileTopBar || shouldShowMobileBottomNav) ? 'auto' : !isMobile && isInboxPage ? '100vh' : isMobile ? '100vh' : 'auto'
        }}
      >
        {/* Desktop App Header */}
        {shouldShowSidebar && !isMobile && (
          <AppHeader
            isGlobalDarkMode={isGlobalDarkMode}
            setIsGlobalDarkMode={setIsGlobalDarkMode}
          />
        )}
        <div className="flex-1 flex flex-col min-h-0">
          <Routes>
          {/* Public Auth Routes */}
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
          <Route path="/reset-password" element={<PublicRoute><ResetPassword /></PublicRoute>} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />

          {/* Root redirect - show login if not authenticated, home if authenticated */}
          <Route path="/" element={<RootRedirect />} />
          {/* Protected Routes */}
          <Route path="/onboarding" element={<ProtectedRoute><OnboardingFlow isGlobalDarkMode={isGlobalDarkMode} /></ProtectedRoute>} />
          <Route path="/preview" element={<ProtectedRoute><BrandPreview isGlobalDarkMode={isGlobalDarkMode} /></ProtectedRoute>} />
          <Route path="/home" element={<ProtectedRoute><Home isGlobalDarkMode={isGlobalDarkMode} /></ProtectedRoute>} />
          <Route path="/crm/home" element={<ProtectedRoute><CrmHome isGlobalDarkMode={isGlobalDarkMode} /></ProtectedRoute>} />
          <Route path="/brand/sitemaps/editor" element={<ProtectedRoute><SitemapEditor isGlobalDarkMode={isGlobalDarkMode} setIsGlobalDarkMode={setIsGlobalDarkMode} /></ProtectedRoute>} />
          <Route path="/brand/sitemaps/editor/:id" element={<ProtectedRoute><SitemapEditor isGlobalDarkMode={isGlobalDarkMode} setIsGlobalDarkMode={setIsGlobalDarkMode} /></ProtectedRoute>} />
          {/* Blueprints editor routes */}
          <Route path="/brand/blueprints/:id/*" element={<ProtectedRoute><DesignStudioEditor isGlobalDarkMode={isGlobalDarkMode} setIsGlobalDarkMode={setIsGlobalDarkMode} /></ProtectedRoute>} />
          <Route path="/brand/design-studio/:id/*" element={<BlueprintRedirect />} />
          <Route path="/brand/design-studio" element={<Navigate to="/brand/blueprints" replace />} />
          <Route path="/brand/*" element={<ProtectedRoute><Brand isGlobalDarkMode={isGlobalDarkMode} setIsGlobalDarkMode={setIsGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/files" element={<ProtectedRoute><Files isGlobalDarkMode={isGlobalDarkMode} /></ProtectedRoute>} />
          {/* Inbox (Overlapping Route) */}
          <Route path="/inbox/*" element={
            <ProtectedRoute>
              {isClient ? (
                <PortalLayout isGlobalDarkMode={isGlobalDarkMode} setIsGlobalDarkMode={setIsGlobalDarkMode}>
                  <PortalInbox isGlobalDarkMode={isGlobalDarkMode} />
                </PortalLayout>
              ) : (
                <Inbox isGlobalDarkMode={isGlobalDarkMode} />
              )}
            </ProtectedRoute>
          } />

          {/* Task Board routes */}
          <Route path="/task-board" element={<ProtectedRoute><Tasks isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/task-board/:slug" element={<ProtectedRoute><TaskBoardPage isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><Calendar isGlobalDarkMode={isGlobalDarkMode} isMobile={isMobile} /></ProtectedRoute>} />
          {/* Boards routes */}
          <Route path="/boards" element={<Navigate to="/task-board" replace />} />
          <Route path="/boards/knowledge-base" element={<ProtectedRoute><KnowledgeBase isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/boards/tickets" element={<Navigate to="/tickets" replace />} />
          <Route path="/boards/time-tracker" element={<ProtectedRoute><Timesheet isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />

          <Route path="/boards/projects" element={<ProtectedRoute><Projects isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/boards/projects/:slug" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
          <Route path="/crm" element={<Navigate to="/crm/leads" replace />} />
          <Route path="/crm/leads" element={<ProtectedRoute><Leads isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/crm/clients" element={<ProtectedRoute><Contacts isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/crm/companies/:id" element={<CompanyRedirect />} />
          <Route path="/deals" element={<ProtectedRoute><Pipelines isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/crm/pipelines" element={<Navigate to="/deals" replace />} />
          <Route path="/pipelines" element={<Navigate to="/deals" replace />} />
          <Route path="/tickets" element={<ProtectedRoute><SupportTickets isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/ticket" element={<Navigate to="/tickets" replace />} />
          <Route path="/knowledge-base" element={<Navigate to="/boards/knowledge-base" replace />} />
          <Route path="/crm/knowledge-base" element={<Navigate to="/boards/knowledge-base" replace />} />
          <Route path="/forms" element={<ProtectedRoute><Forms isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/crm/forms" element={<Navigate to="/forms" replace />} />
          {/* Proposals Routes */}
          <Route path="/proposals" element={
            <ProtectedRoute>
              {isClient ? (
                <PortalLayout isGlobalDarkMode={isGlobalDarkMode} setIsGlobalDarkMode={setIsGlobalDarkMode}>
                  <PortalProposals />
                </PortalLayout>
              ) : (
                <Proposals isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} />
              )}
            </ProtectedRoute>
          } />
          <Route path="/proposals/:id/edit" element={<ProtectedRoute><ProposalEditor /></ProtectedRoute>} />
          <Route path="/proposals/:id/preview" element={<ProtectedRoute><ProposalPreview /></ProtectedRoute>} />
          <Route path="/crm/proposals" element={<Navigate to="/proposals" replace />} />
          <Route path="/board/proposal" element={<Navigate to="/proposals" replace />} />
          <Route path="/board/proposals" element={<Navigate to="/proposals" replace />} />
          <Route path="/boards/proposals" element={<Navigate to="/proposals" replace />} />
          <Route path="/boards/proposals/:id/edit" element={<ProposalLegacyEditRedirect />} />
          <Route path="/boards/proposals/:id/preview" element={<ProposalLegacyPreviewRedirect />} />
          <Route path="/boards/forms" element={<Navigate to="/forms" replace />} />
          <Route path="/sites/hosting" element={<ProtectedRoute><Hosting isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/services" element={<Navigate to="/services/catalog" replace />} />
          <Route path="/services/catalog" element={<ProtectedRoute><Services isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/crm/invoices" element={<ProtectedRoute><Invoices isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/invoices" element={<Navigate to="/crm/invoices" replace />} />
          <Route path="/services/invoices" element={<Navigate to="/crm/invoices" replace />} />
          <Route path="/services/subscriptions" element={<ProtectedRoute><div className="flex-1 p-8 text-center text-muted-foreground"><h2 className="text-2xl font-bold mb-2">Subscriptions</h2><p>Subscriptions module coming soon.</p></div></ProtectedRoute>} />
          <Route path="/services/orders" element={<ProtectedRoute><div className="flex-1 p-8 text-center text-muted-foreground"><h2 className="text-2xl font-bold mb-2">Orders</h2><p>Orders module coming soon.</p></div></ProtectedRoute>} />
          <Route path="/services/coupons" element={<ProtectedRoute><div className="flex-1 p-8 text-center text-muted-foreground"><h2 className="text-2xl font-bold mb-2">Coupons</h2><p>Coupons module coming soon.</p></div></ProtectedRoute>} />
          <Route path="/sites" element={<ProtectedRoute><Sites isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/sites/updates" element={<ProtectedRoute><Sites isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/sites/backups" element={<ProtectedRoute><Sites isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/sites/reports" element={<ProtectedRoute><Sites isGlobalDarkMode={isGlobalDarkMode} onMobileSubMenuToggle={handleMobileSubMenuOpen} isMobile={isMobile} /></ProtectedRoute>} />
          <Route path="/sites/:siteId/*" element={<ProtectedRoute><SiteDetails isGlobalDarkMode={isGlobalDarkMode} /></ProtectedRoute>} />
          
          {/* Client Portal Specific Routes */}
          <Route path="/proposals/:slug" element={<ProtectedRoute><PortalProposalView /></ProtectedRoute>} />
          
          <Route element={<ProtectedRoute><PortalLayout isGlobalDarkMode={isGlobalDarkMode} setIsGlobalDarkMode={setIsGlobalDarkMode} /></ProtectedRoute>}>
            <Route path="/dashboard" element={<PortalDashboard />} />
            <Route path="/projects" element={<PortalProjects />} />
            <Route path="/projects/:slug" element={<ProjectDetail />} />
            <Route path="/support" element={<PortalSupport />} />
          </Route>

          {/* Account Settings (Overlapping Route) */}
          <Route path="/account-settings" element={
            <ProtectedRoute>
              {isClient ? (
                <PortalAccountSettings isMobile={isMobile} />
              ) : (
                <AccountSettings isGlobalDarkMode={isGlobalDarkMode} isMobile={isMobile} />
              )}
            </ProtectedRoute>
          } />
          </Routes>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      {shouldShowMobileBottomNav && (
        <MobileBottomNav
          isGlobalDarkMode={isGlobalDarkMode}
          onSubMenuOpen={handleMobileSubMenuOpen}
          activeParentMenuId={activeParentMenuId}
        />
      )}

      {/* Mobile Sub Menu */}
      {shouldShowMobileBottomNav && showMobileSubMenu && (
        <MobileSubMenu
          isGlobalDarkMode={isGlobalDarkMode}
          subItems={mobileSubMenuItems}
          parentLabel={activeParentMenuLabel || undefined}
          hasTopBar={shouldShowMobileTopBar}
          onClose={() => {
            setShowMobileSubMenu(false);
            setActiveParentMenuId(null);
            setActiveParentMenuLabel(null);
          }}
        />
      )}

      {/* Settings Modal (Global) */}
      {new URLSearchParams(location.search).get('modal') === 'settings' && (
        isClient ? (
          <PortalAccountSettings isMobile={isMobile} />
        ) : (
          <AccountSettings isGlobalDarkMode={isGlobalDarkMode} isMobile={isMobile} />
        )
      )}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <WorkspaceProvider>
          <QueryClientProvider client={queryClient}>
            <HelmetProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                  <DashboardProvider>
                    <BrandProvider>
                      <TeamChatProvider>
                        <AppContent />
                      </TeamChatProvider>
                    </BrandProvider>
                  </DashboardProvider>
                </Router>
              </TooltipProvider>
            </HelmetProvider>
          </QueryClientProvider>
        </WorkspaceProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;